import React , { JSX } from 'react';
import ProgressionBar from '../ProgressionBar/progressionBar';
import { ComponentWithContentProps } from '../../types';
import Styles from './MultiItemAccessoriesPageStyle.module.scss';
import RichText from '../../richtext';
import { useEffect, useState, useRef } from 'react';
import AccessoryCard from '../Accessory-card/AccessoryCard';
import AccessoryPreviewCardLoader from '../AccessoryPreviewCardLoader/AccessoryPreviewCardLoader';
import PaymentTypeTabs from '../PaymentTypeTabs/PaymentTypeTabs';
import storyBlokService from '../../../services/MultiItemAccessories/storyBlokService';
import { getCookie } from 'cookies-next';
import { ACCESSORY, BASE, CONTRACT, HARDWARE, INTERNETFLOW, MOBILERATEPLAN, ONE_ECOMMERCE_HARDWARE_SUMMARY, TABLETS, TV, WATCHES } from '../../../utils/constants';
import Row from '../../ResponsiveRow/row';
import { MOBILEACCESSORIES, MONTHLY } from '../../../utils/constants';
import { setMmcItemTracker, trackNewDatalayerEvent, trackViewItem } from '../../..//utils/AnalyticsUtils';
import { CONTENT, CONTINUE, CTABUTTONCLICK, CTATEXTCLICK, GAEVENT, INFO, LEARNMORE, LINK, OPENPOPUP, POPUP, TABCLICK, VIEWEACHITEM } from '../../../Analytics/AnalyticsConstants';
import { useMultiAccessoryContext } from '../multiItemAccessoryStore/useMultiAccessoryContext';
import { useMultiAccessoryActions } from '../multiItemAccessoryStore/useMultiAccessoryActions';
import MultiAccessoryProvider from '../multiItemAccessoryStore/MultiAccessoryProvider';
import { MultiItemAccessoriesSchema } from './MultiItemAccessoriesSchema';
import { useMultiAccessoryUpdateCartActions } from '../multiItemAccessoryStore/useMultiAccessoryUpdateCartActions';
import Dotloader from '../../Dotloader/Dotloader';
import { countZero, getSBAssetUrl, replacePlaceholder, rp_lastcartItemWithParentPOType, viewport } from '../../../utils/utils';
import AccessoryModal from '../Accessory-card/AccessoryModal';
import FalconButtonV2 from '../../FalconButtonV2/FalconButtonV2';
import { AssetPathPropConfig } from '../../../services';
import AccessoryUpdateCartDotloader from '../AccessoryUpdateCartDotloader/AccessoryUpdateCartDotloader';

// Export the schema for backward compatibility
// export type { MultiItemAccessoriesSchema } from './MultiItemAccessoriesSchema';

// Extended props interface to include setAccessoriesSelected as a separate prop
export interface MultiItemAccessoriesProps extends ComponentWithContentProps<MultiItemAccessoriesSchema> {
  setAccessoriesSelected?: (accessories: any[]) => void;
}

// eslint-disable-next-line max-lines-per-function
const MultiItemAccessories = ({ content, metadata }: MultiItemAccessoriesProps): JSX.Element => {
  const activelocaleId: string | undefined = metadata?.lang;
  const [deviceLoader, setDeviceLoader] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<string | null>(null); // 'WATCHES' or 'TABLETS' or null
  const cartId = getCookie('one-commerce-cartId');
  const [hardwareName, setHardwareName] = useState('');
  const [isSubscriptionFlow, setIsSubscriptionFlow] = useState<boolean>(false);
  // useRef: read sessionStorage once client-side — synchronously available everywhere,
  // avoids SSR hydration mismatch and async state-update timing issues
  const hardwareSessionObjRef = useRef<any>({});
  const { store, dispatch } = useMultiAccessoryContext();
  const actions = useMultiAccessoryActions(dispatch);
  const cartActions = useMultiAccessoryUpdateCartActions();
  const { recommendedAccessories, isFetchingRecommendedAccessories, deviceSessionValues, multiItemAccessoriessList, loading, isLoadingCartData , cartData, previousTab } = store;
  // Todo Get the correct data based on selected tab - for ACCESSORY use direct data, for others use hardwares property
  const selectedTabData = recommendedAccessories?.[store?.selectedTab]?.hardware;
  const basicConfigUrl = AssetPathPropConfig?.getInstance()?.getAssetPath();
  // Helper function to get modal popup content based on tab type
  const getModalContent = () => {
    return showInfoModal === WATCHES ? content?.watchesPopup?.[0] : content?.tabletsPopup?.[0];
  };
 
  const sbRecommendations: any[] = content?.recommendationType;
  // Create recommendationType object based on filteredSbRecommendations
  const recommendationType: any = {};
  
  // Single effect: read sessionStorage first, then kick off all init logic
useEffect(() => {
  setIsMounted(true);
  // Read sessionStorage client-side only to avoid SSR/hydration mismatch
  const sessionStr = sessionStorage?.getItem(ONE_ECOMMERCE_HARDWARE_SUMMARY);
  const sessionObj = sessionStr ? JSON.parse(sessionStr) : {};
  // Store in ref for synchronous access throughout the component lifecycle
  hardwareSessionObjRef.current = sessionObj;
  const deviceSessionValue = { ...sessionObj, cartId: cartId };
  actions.setDeviceSessionValues(deviceSessionValue);
  actions?.setSelectedTab(content?.defaultTabSelection);
  setHardwareName(sessionObj?.hardwareVariant?.note?.filter((item: any) => item.author === 'hardwareGroupName')?.[0]?.text);
  // Kick off TUA data load after session is ready
  setDeviceLoader(true);
  if (cartId) {
    fetchandSetRecommendations(undefined, sessionObj);
  }
}, []);

  const fetchandSetRecommendations = (tabKey?: any, sessionObj?: any) => {
    // Use passed sessionObj (on initial load) or fall back to ref (on tab switch)
    const currentSessionObj = sessionObj ?? hardwareSessionObjRef.current;
    if (tabKey) {
      actions.setPreviousTab(store?.selectedTab); // Update previousTab in store before changing tab
      actions.setSelectedTab(tabKey);
    }
    if (tabKey) {
      if((recommendedAccessories?.[tabKey]?.hardware || [])?.length <= 0)
      {
      setDeviceLoader(true);
      actions.getRecommendedAccessory({ cartId, recommendationsType: tabKey });
      }
    if (loading) return; // Prevent new API calls if one is already running
    } else {
      cartActions.fetchCartData(cartId).then((res: any) => {
        const cartRecommendationsType = res?.cartRecommendationsType ?? [content?.defaultTabSelection];
        const selectedContractType = currentSessionObj?.hardwareVariant?.selectedContractType;
        const structuredCartData = rp_lastcartItemWithParentPOType(res?.cartItem ?? [],{},{},{});
        const subscriptionFlow = !(structuredCartData?.MOBILE_ORDER?.HARDWARE || structuredCartData?.STANDALONE_HARDWARE);
        setIsSubscriptionFlow(subscriptionFlow)
        const contractTerm  = Number.parseFloat(structuredCartData?.MOBILE_ORDER?.HARDWARE?.itemTerm?.[0]?.duration?.amount) || 0
        // Filter sbRecommendations based on contract type(only for monthly) and for new subcriptions
        const filteredSbRecommendations = (
          // selectedContractType === CONTRACT
          contractTerm > 0
           && ((res?.cartItem?.[0]?.processType?.id === "DEVICE_RENEWAL" || res?.cartItem?.[0]?.processType?.id === "DEVICE_RP_RENEWAL") ? content?.enableExistingSubscription : true))
          ? sbRecommendations?.filter((item: string) => cartRecommendationsType.includes(item))
          : sbRecommendations?.filter((item: string) => item === ACCESSORY && cartRecommendationsType.includes(item));
        filteredSbRecommendations?.forEach((type: string) => {
          recommendationType[type] = [];
        });
        // Store recommendationType in state or ref if needed elsewhere
        actions.setRecommendedAccessories(recommendationType);
        const defaultTab = 
          selectedContractType === CONTRACT ? filteredSbRecommendations?.includes(content?.defaultTabSelection) 
            ? content?.defaultTabSelection 
            : filteredSbRecommendations[0] : ACCESSORY;
        actions?.setSelectedTab(defaultTab);
        actions.getRecommendedAccessory({ cartId, recommendationsType: tabKey ? tabKey : defaultTab});
      }).catch(() => {
        // Redirect to summary page if getCart fails, same as recommendations failure
        const localIsSubscriptionFlow = [MOBILERATEPLAN, INTERNETFLOW, TV]?.includes?.(selectedTabData?.[0]?.recommendationProcessingType) ? true : (currentSessionObj?.hardwareVariant ? false : true);
        if (typeof window !== "undefined") {
          window.location.href = localIsSubscriptionFlow ? content?.winCheckoutUrl?.cached_url : content?.multiItemCheckoutUrl?.url;
        }
      });
    }
  }
  // to map storyblok and api data in store
  useEffect(() => {
    if ((recommendedAccessories?.[recommendedAccessories?.tabkey]?.hardware?.length) > 0) {
      if (recommendedAccessories?.tabkey === ACCESSORY) {
        // mapping of accessories according to recommendation api response
        storyBlokService?.getStoryBlokData(
          activelocaleId,
          recommendedAccessories?.[recommendedAccessories?.tabkey]?.hardware?.map((value: any) => value?.baseProducts),
          content?.parentSlugUrl
        ).then((res: any) => {
          sbRecommendationMapping(res,recommendedAccessories?.tabkey);
        });
      } else {
        const masterlst = recommendedAccessories?.tabkey === WATCHES ? content?.watchMasterList?.[0]?.reference?.content :
        content?.tabletMasterList?.[0]?.reference?.content
        sbRecommendationMapping(masterlst,recommendedAccessories?.tabkey);
      }
    } else {
      // redirect to summary page if no accessories are returned from backend
      if ((!isFetchingRecommendedAccessories || !cartId) && !isLoadingCartData && typeof window !== "undefined") {
        window.location.href = isSubscriptionFlow ? content?.winCheckoutUrl?.cached_url : content?.multiItemCheckoutUrl?.url;
      }
    }
  }, [recommendedAccessories])

  const sbRecommendationMapping = (response: any,tabKey: any) => {
    const accessoryList = (recommendedAccessories?.[tabKey]?.hardware || [])
      ?.filter((item: any) =>
        deviceSessionValues?.hardwareVariant?.selectedContractType === CONTRACT
          ? item?.recurringPrice?.monthlyInstallment > 0
          : item
      )
      ?.reduce((acc: any, value: any, currentIndex: number) => {
        const deviceDetails = {
          image: '',
          type: MOBILEACCESSORIES
        };
        let accessoryMappingSb;
        if(tabKey === ACCESSORY)
        {
        accessoryMappingSb = 
        response?.sbStory?.data?.stories?.filter((val: any) => value?.baseProducts === val?.content?.productId)?.[0];
        const requestedVariant = accessoryMappingSb?.content?.body?.[0]?.deviceList?.find((deviceVariant: any) => deviceVariant?.devicePlanId === value?.id);
        deviceDetails.image = requestedVariant?.promoImageGallery?.[0]?.slideImages?.[0]
          ? requestedVariant?.promoImageGallery?.[0]?.slideImages?.[0]?.image?.filename
          : requestedVariant?.imageGallery?.[0]?.slideImages?.[0]?.image?.filename;
        deviceDetails.type = value?.content?.type ?? MOBILEACCESSORIES;
        }
        else{
          accessoryMappingSb = response?.body?.filter((val: any) => value?.baseProducts === val?.m_p_id)?.[0];
          deviceDetails.image = accessoryMappingSb?.images?.[0]?.filename
          deviceDetails.type = value?.content?.type ?? MOBILEACCESSORIES;
        }
        if (accessoryMappingSb) {
          acc.push({ sbStoryItem: accessoryMappingSb, imageData: deviceDetails, recomApiResponse: value });
          trackViewItem(VIEWEACHITEM, VIEWEACHITEM, {
            displayName: value?.name,
            productId: value?.id,
            priceValue: value?.oneTimePrice?.base,
            index: currentIndex,
            variant: value?.variantCategory?.color,
            storage: value?.variantCategory?.storage,
          });
        }
        return acc;
      }, []);
    if (accessoryList?.length > 0 && (response?.sbStory?.data?.stories || response?.body)?.length > 0) {
      actions.setMultiItemAccessoriessList({...multiItemAccessoriessList, [tabKey]: accessoryList });
    } else {
      // redirect to summary page if no accessories is eligbile
      window.location.href = isSubscriptionFlow ? content?.winCheckoutUrl?.cached_url : content?.multiItemCheckoutUrl?.url
    }
    setDeviceLoader(false);
    trackViewItemList(accessoryList);
  }

  //select first payment option as default initially unless provided in recommendation api
  useEffect(() => {
  (multiItemAccessoriessList?.[store?.selectedTab] || [])?.filter((item: any) => {
      if (item?.recomApiResponse?.paymentType?.toLowerCase()) {
        actions.setSelectedPaymentType(item?.recomApiResponse?.paymentType.toLowerCase() === MONTHLY ? CONTRACT : BASE);
      }
    }
    )
  }, [multiItemAccessoriessList]);

  const checkingviewPort = viewport();
  const findView = () => {
    //function to return no of cards in a row based on view
    if (checkingviewPort === 'large-desktop') {
      return 3;
    } else if (checkingviewPort === 'desktop') {
      return 3;
    } else if (checkingviewPort === 'tablet') {
      return 2;
    } else if (checkingviewPort === 'mobile') {
      return 1;
    }
    return 0;
  };
  const view: any = findView();

  const selectedRatePlan = recommendedAccessories?.[store?.selectedTab]?.ratePlan;
  const infoBannerPriceRaw = selectedRatePlan?.derivedPrice ?? selectedRatePlan?.listPrice ?? 0;

  // this is for structuring the event tracking data
  const trackViewItemList = (accessoryList: any) => {
    const itemList = accessoryList?.map((item: any, index: number) => {
      return ({
        displayName: item?.recomApiResponse?.name,
        productId: item?.recomApiResponse?.id,
        priceValue: item?.recomApiResponse?.oneTimePrice?.base,
        index: index,
        variant: item?.recomApiResponse?.variantCategory?.color,
        storage: item?.recomApiResponse?.variantCategory?.storage,
      })
    })
    setMmcItemTracker(itemList);
  }

  // Todod tab title should always be in english
  const handleTabClick = (tabKey: string, tabTitle: string) => {
    // Determine analytics tab title based on selection and Storyblok content
    let analyticsTabTitle = tabTitle;
    if (tabKey === ACCESSORY && content?.accessoryAnalyticsTitle) {
      analyticsTabTitle = content.accessoryAnalyticsTitle;
    } else if (tabKey === TABLETS && content?.tabletsAnalyticsTitle) {
      analyticsTabTitle = content.tabletsAnalyticsTitle;
    } else if (tabKey === WATCHES && content?.watchesAnalyticsTitle) {
      analyticsTabTitle = content.watchesAnalyticsTitle;
    } 
    trackNewDatalayerEvent(GAEVENT, TABCLICK, {
      component: CONTENT,
      click_text: analyticsTabTitle
    });
    fetchandSetRecommendations(tabKey);
  };

  return (
    <>
      {loading && (store?.selectedTab === previousTab ? <Dotloader /> : <AccessoryUpdateCartDotloader content={content} />)}
       {/* Only render the main page container when mounted client-side and data is loading/ready */}
      {(isMounted && content && (multiItemAccessoriessList?.[store?.selectedTab]?.length > 0 || deviceLoader)) && (
      <div className={`${Styles.mainPage}`}>
        {content?.progressionBar?.[0] && (
          <ProgressionBar
        content={content?.progressionBar?.[0]}
          />
        )}
        <Row>
          <div>
        <div className={`${Styles.title}`}>
          <RichText content={content?.recommendationLabel} />
        </div>
        {Object.keys(recommendedAccessories || {}).filter((key: string) => sbRecommendations?.includes(key)).length <= 1 && !isSubscriptionFlow && (
          <div className={`${Styles.deviceText}`}>
            {content?.compatibleText}
            <span className={Styles.deviceName}>{hardwareName}</span>
          </div>
        )}
          </div>
        </Row>
        {/* Tabs based on recommendedAccessories, ordered by recommendationType from Storyblok */}
        {(Object.keys(recommendedAccessories || {}).filter((key: string) => sbRecommendations?.includes(key)).length > 1) && (
            <div className={Styles.tabsContainer}>
            {(content?.recommendationType || [])
              ?.filter((tabKey: string) => recommendedAccessories?.[tabKey])
              ?.map((tabKey: string) => {
                // Count from cartData (real-time source of truth)
                const totalCount = Object.keys(cartData?.[tabKey] || {}).length;

                // Get tab title from Storyblok content
                const tabTitle = tabKey === ACCESSORY ? content?.accessoryTitle
                  : tabKey === WATCHES ? content?.watchesTitle
                  : tabKey === TABLETS ? content?.tabletsTitle
                  : tabKey;
                return (
                  <button
                    key={tabKey}
                    className={`${Styles.tabButton} ${store.selectedTab === tabKey ? Styles.activeTab : ''}`}
                    onClick={() => {
                      if (store.selectedTab !== tabKey) {
                        handleTabClick(tabKey, tabTitle);
                      }
                    }}
                    disabled={!recommendedAccessories?.[tabKey]}
                  >
                    {tabTitle}
                    {totalCount > 0 && (
                    <span className={Styles.tabCount}>
                      {totalCount}
                    </span>
                    )}
                  </button>
                );
              })}
            </div>
        )}
        {/* Information banner below tabs - for Smartwatches and Tablets */}
        {((store.selectedTab === WATCHES && content?.watchesInfoBannerText) || 
          (store.selectedTab === TABLETS && content?.tabletsInfoBannerText)) && (
          <div className={Styles.infoBanner}>
            <div className={Styles.infoBannerContent}>
              <img className={Styles.infoIcon} src={content?.esimIcon?.filename} />
                <span>
                  <span>
                    {replacePlaceholder(
                      store.selectedTab === WATCHES ? content?.watchesInfoBannerText : content?.tabletsInfoBannerText,
                      [countZero(infoBannerPriceRaw)]
                    )}
                  </span>
                  <span className={Styles.learnMoreText} onClick={() => {
                    trackNewDatalayerEvent(GAEVENT, CTATEXTCLICK, {
                      component: LINK,
                      click_text: LEARNMORE
                    });
                    setShowInfoModal(store.selectedTab);
                  }}>
                    {content?.learnMoreText}
                  </span>
                </span>
            </div>
          </div>
        )}


        {isSubscriptionFlow && (
          <PaymentTypeTabs
            content={{
              paymentHeadingText: content?.paymentHeadingText,
              paymentOptions: content?.paymentOptions,
              paymentTypeChngePopup: content?.paymentTypeChngePopup?.[0],
              paymentTypeChngePopupOnetime: content?.paymentTypeChngePopupOnetime?.[0],
            }}
          />
        )}
        {(deviceLoader && !multiItemAccessoriessList?.[store?.selectedTab]?.length) ? (
          <AccessoryPreviewCardLoader cards={view} />
        ) : (
              <AccessoryCard
                content={content}
                tabIndex={1}
                checkingviewPort={checkingviewPort}
				        isSubscriptionFlow={isSubscriptionFlow}
              />
        )}
      </div>
      )}
      
      {/* Info Modal for Watches and Tablets */}
      {showInfoModal && (() => {
        const modalContent = getModalContent();
        trackNewDatalayerEvent(GAEVENT, OPENPOPUP, {
          component: POPUP,
          message_title: modalContent?.heading?.content?.[0]?.content?.[0]?.text?.slice(0, 100),
          message_description: modalContent?.description?.content?.[0]?.content?.[0]?.text?.slice(0, 100),
          message_type: INFO,
        });
        return (
          <AccessoryModal
            modalState={!!showInfoModal}
            handler={() => setShowInfoModal(null)}
            showFooter={false}
            height={'auto'}
            fixedWidth={(checkingviewPort === 'large-desktop' || checkingviewPort === 'desktop' || checkingviewPort === 'tablet') ? '624px' : ''}
            noCloseButton={false}
            backDrop={true}
          >
            <div className={Styles.watchInfoModal}>
              {modalContent?.heading && (
                <div className={Styles.modalHeading}>
                  <RichText content={modalContent.heading} />
                </div>
              )}
              
              {modalContent?.description && (
                <div className={Styles.modalDescription}>
                  <RichText content={modalContent.description} />
                </div>
              )}
              
              <div className={Styles.modalButtons}>
                {modalContent?.link && (
                  <a 
                    href={getSBAssetUrl(basicConfigUrl, modalContent.link?.cached_url || modalContent.link?.url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={Styles.factSheetLink}
                  >
                    {modalContent.linkText}
                  </a>
                )}
                
                {modalContent?.button?.length > 0 && (
                  <FalconButtonV2
                    content={modalContent.button[0]}
                    onClick={() => {
                      trackNewDatalayerEvent(GAEVENT, CTABUTTONCLICK, {
                        component: LINK,
                        click_text: CONTINUE
                      });
                      setShowInfoModal(null)
                    }}
                    metadata={undefined}
                    wafIdentifier={''}
                  />
                )}
              </div>
            </div>
          </AccessoryModal>
        );
      })()}
    </>
  );
}

// HOC Pattern: Wrap the component with provider at export
const MultiItemAccessoriesWithProvider = (props: MultiItemAccessoriesProps) => (
  <MultiAccessoryProvider>
    <MultiItemAccessories {...props} />
  </MultiAccessoryProvider>
);

export default MultiItemAccessoriesWithProvider;